import { DEPOSIT_STATUSES, WITHDRAWAL_STATUSES } from '@pf/contracts';
import { ArrowDownToLine, ArrowUpFromLine, History, Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerActivity } from '@pf/bff-contract';
import { useActivity, useDeposit } from '@/lib/api/bff-queries';
import { bankInfo } from '@/lib/banks';
import { formatDateTime, formatRelative } from '@/lib/datetime';
import { humanizeEnum, shortId } from '@/lib/utils';
import { MoneyDisplay } from '@/components/domain/money-display';
import { StateBadge } from '@/components/domain/state-badge';
import { StateTimeline } from '@/components/domain/state-timeline';
import { DataTable, type Column } from '@/components/layout/data-table';
import { Field, PageHeader } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from '@/components/ui/feedback';
import {
  Code,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';

/**
 * Transaction history — a filterable list plus a per-transaction state timeline.
 *
 * The timeline is the point of the page. A status alone ("Under review") tells the customer
 * nothing about *when* things happened or how close a deadline was; the event log tells the whole
 * story, and it is the same log the operator sees, because SAD §13 makes the hash-chained event
 * store the single record of truth rather than a per-audience summary.
 *
 * Deposits and withdrawals are interleaved deliberately. To the customer they are one ledger; the
 * fact that they are two different state machines internally is PaymentFlow's problem, not
 * theirs — but the badges still name the correct machine so nothing is misrepresented.
 */
export function HistoryPage(): ReactNode {
  const { t } = useTranslation();
  const [kind, setKind] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<CustomerActivity | null>(null);

  const activity = useActivity({ kind, status, q });
  const items = activity.data?.items ?? [];

  const statusOptions =
    kind === 'WITHDRAWAL'
      ? WITHDRAWAL_STATUSES
      : kind === 'DEPOSIT'
        ? DEPOSIT_STATUSES
        : [...new Set([...DEPOSIT_STATUSES, ...WITHDRAWAL_STATUSES])];

  const columns: Column<CustomerActivity>[] = [
    {
      id: 'summary',
      header: t('history.columns.transaction'),
      priority: 'primary',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
            aria-hidden="true"
          >
            {row.kind === 'DEPOSIT' ? (
              <ArrowDownToLine className="size-4" />
            ) : (
              <ArrowUpFromLine className="size-4" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium">
              {row.kind === 'DEPOSIT' ? t('history.kindDeposit') : t('history.kindWithdrawal')}
            </span>
            <span className="block truncate font-mono text-2xs text-muted-foreground">
              {shortId(row.id)}
            </span>
          </span>
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('history.columns.amount'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <MoneyDisplay amount={row.amount} />,
    },
    {
      id: 'status',
      header: t('history.columns.status'),
      priority: 'secondary',
      cell: (row) => (
        <StateBadge
          machine={row.kind === 'DEPOSIT' ? 'deposit' : 'withdrawal'}
          status={row.status}
          size="sm"
        />
      ),
    },
    {
      id: 'bank',
      header: t('history.columns.bank'),
      priority: 'detail',
      cell: (row) =>
        row.counterparty_bank ? (
          <span className="text-xs">{bankInfo(row.counterparty_bank).name}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: 'when',
      header: t('history.columns.updated'),
      priority: 'detail',
      cell: (row) => (
        <span
          className="whitespace-nowrap text-xs text-muted-foreground"
          title={formatDateTime(row.updated_at)}
        >
          {formatRelative(row.updated_at)}
        </span>
      ),
    },
    {
      // The row itself is clickable for the mouse, but a focusable control is what keyboard and
      // screen-reader users actually navigate with — see the note on `DataTable`.
      id: 'open',
      header: '',
      srHeader: t('history.columns.detailsSrHeader'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('history.detailsAriaLabel', {
            kind:
              row.kind === 'DEPOSIT' ? t('history.kindDepositLower') : t('history.kindWithdrawalLower'),
            id: shortId(row.id),
          })}
          onClick={() => {
            setSelected(row);
          }}
        >
          {t('history.detailsButton')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('history.title')} description={t('history.description')} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="history-search">{t('history.searchLabel')}</Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  id="history-search"
                  className="pl-9"
                  placeholder={t('history.searchPlaceholder')}
                  value={q}
                  onChange={(event) => {
                    setQ(event.target.value);
                  }}
                />
              </div>
            </div>

            <div className="w-full space-y-1.5 sm:w-40">
              <Label htmlFor="history-kind">{t('history.typeLabel')}</Label>
              <Select
                value={kind}
                onValueChange={(value) => {
                  setKind(value);
                  setStatus('ALL');
                }}
              >
                <SelectTrigger id="history-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('history.typeOptions.all')}</SelectItem>
                  <SelectItem value="DEPOSIT">{t('history.typeOptions.deposits')}</SelectItem>
                  <SelectItem value="WITHDRAWAL">{t('history.typeOptions.withdrawals')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-full space-y-1.5 sm:w-48">
              <Label htmlFor="history-status">{t('history.statusLabel')}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="history-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t('history.statusOptions.all')}</SelectItem>
                  {statusOptions.map((value) => (
                    <SelectItem key={value} value={value}>
                      {humanizeEnum(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {kind !== 'ALL' || status !== 'ALL' || q !== '' ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setKind('ALL');
                  setStatus('ALL');
                  setQ('');
                }}
              >
                <X aria-hidden="true" />
                {t('history.clearButton')}
              </Button>
            ) : null}
          </div>

          {activity.isLoading ? (
            <TableSkeleton rows={8} columns={5} />
          ) : activity.isError ? (
            <ErrorState
              error={activity.error}
              onRetry={() => {
                void activity.refetch();
              }}
            />
          ) : (
            <>
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {t('history.resultCount', {
                  count: items.length,
                  total: activity.data?.total ?? 0,
                })}
              </p>
              <DataTable
                caption={t('history.tableCaption')}
                columns={columns}
                rows={items}
                rowKey={(row) => row.id}
                onRowClick={setSelected}
                isRowSelected={(row) => row.id === selected?.id}
                empty={
                  <EmptyState
                    icon={<History className="size-5" aria-hidden="true" />}
                    title={
                      q !== '' || kind !== 'ALL' || status !== 'ALL'
                        ? t('history.empty.filteredTitle')
                        : t('history.empty.noneTitle')
                    }
                    description={
                      q !== '' || kind !== 'ALL' || status !== 'ALL'
                        ? t('history.empty.filteredDescription')
                        : t('history.empty.noneDescription')
                    }
                  />
                }
              />
            </>
          )}
        </div>

        <div className="lg:sticky lg:top-24 lg:h-fit">
          <DetailPanel
            activity={selected}
            onClose={() => {
              setSelected(null);
            }}
          />
        </div>
      </div>
    </>
  );
}

function DetailPanel({
  activity,
  onClose,
}: {
  activity: CustomerActivity | null;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const deposit = useDeposit(activity?.kind === 'DEPOSIT' ? activity.id : '');

  if (!activity) {
    return (
      <Card>
        <CardContent className="pt-5">
          <EmptyState
            icon={<History className="size-5" aria-hidden="true" />}
            title={t('history.detail.emptyTitle')}
            description={t('history.detail.emptyDescription')}
            className="border-0 bg-transparent py-8"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
        <div className="min-w-0 space-y-1">
          <CardTitle>
            {activity.kind === 'DEPOSIT' ? t('history.kindDeposit') : t('history.kindWithdrawal')}
          </CardTitle>
          <Code className="block truncate">{activity.id}</Code>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('history.detail.closeAriaLabel')}
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <dl className="grid grid-cols-2 gap-4">
          <Field label={t('history.detail.fields.amount')}>
            <MoneyDisplay amount={activity.amount} size="lg" />
          </Field>
          <Field label={t('history.detail.fields.status')}>
            <StateBadge
              machine={activity.kind === 'DEPOSIT' ? 'deposit' : 'withdrawal'}
              status={activity.status}
              size="sm"
            />
          </Field>
          <Field label={t('history.detail.fields.reference')}>
            <Code>{activity.reference}</Code>
          </Field>
          <Field label={t('history.detail.fields.created')}>
            <span className="text-xs">{formatDateTime(activity.created_at)}</span>
          </Field>
        </dl>

        <div className="space-y-2">
          <h3 className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('history.detail.eventTimeline')}
          </h3>
          {activity.kind === 'WITHDRAWAL' ? (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('history.detail.withdrawalNote')}
            </p>
          ) : deposit.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-8" />
              ))}
            </div>
          ) : deposit.isError ? (
            <ErrorState error={deposit.error} />
          ) : (
            <StateTimeline events={deposit.data?.events ?? []} dense />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
